/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import useAuthHttp from "../../hooks/useAuthHttp";
import { IAssetInfo } from "../../models/asset";
import { useFormik } from "formik";
import * as Yup from "yup";
import View from "../Container";
import { Toast } from "primereact/toast";
import Nav, { NavItem } from "../Nav";
import Button from "../Button";
import Text from "../Text";
import { useHomeContext } from "../../contexts/HomeContext";
import { IVehicleModelYear } from "../../hooks/useFindVehicleModel";
import { IVehicleResult } from "../../commons/vehicle";
import ButtonGroup from "../ButtonGroup";
import VehicleBrandSearchSelector from "../VehicleBrandSearchSelector";
import VehicleModelSearchSelector from "../VehicleModelSearchSelector";
import FormField from "../FormField";
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedVehicle } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";

interface IVehicleHandleForm {
    groupType?: {
        id: number;
        name: string;
    };
    fuelType?: {
        id: number;
        name: string;
    },
    brand?: {
        id: string;
        name: string;
        code: string;
    };
    model?: {
        id: string;
        name: string;
        code: number;
        years: IVehicleModelYear[];
    };
    purchaseDate?: Date;
    year?: {
        id: string;
        label: string;
    };
    purchaseValue?: number;
    alias?: string;
}

interface IVehicleHandleFormProps {
    onSuccessRegister(assetInfo: IVehicleHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedVehicle;
}

const VehicleHandleForm = (props: IVehicleHandleFormProps) => {
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();
    const http = useAuthHttp();
    const [isEdit, setIsEdit] = useState(false);
    const formik = useFormik<IVehicleHandleForm>({
        initialValues: {
            groupType: undefined,
            fuelType: undefined,
            brand: undefined,
            model: undefined,
            year: undefined,
            alias: "",
            purchaseValue: 0,
            purchaseDate: undefined
        },
        validationSchema: Yup
            .object()
            .shape({
                groupType: Yup
                    .object()
                    .required("Campo obrigatório"),
                fuelType: Yup
                    .object()
                    .required("Campo obrigatório"),
                brand: Yup
                    .object()
                    .required("Campo obrigatório"),
                model: Yup
                    .object()
                    .required("Campo obrigatório"),
                year: Yup
                    .object()
                    .required("Campo obrigatório"),
                alias: Yup
                    .string()
                    .required("Campo obrigatório"),
                purchaseValue: Yup
                    .number()
                    .min(1, "Valor obrigatório")
                    .required("Campo obrigatório"),
                purchaseDate: Yup
                    .string()
                    .required("Campo obrigatório")
            }),
        onSubmit: async (form) => await send(form)
    });

    const vehicleDetailsQuery = useQuery({
        queryKey: ["vehicleDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                setIsEdit(false);
                return null;
            }

            setIsEdit(true);
            return await http.doGet<IVehicleResult>(`/vehicle/user/${props.assetId}`);
        }
    });

    const vehicleInsertMutation = useMutation({
        mutationFn: async (form: IVehicleHandleForm) => {
            const body = {
                vehicle_type_desc: form.groupType!.name,
                vehicle_type: form.groupType!.id,
                vehicle_fuel_desc: form.fuelType,
                vehicle_fuel_type: form.fuelType!.id,
                vehicle_brand_id: form.brand!.id,
                vehicle_model_id: form.model!.id,
                vehicle_year: moment(form.year?.id, "YYYY-MM").format("YYYY-MM-DD HH:mm:ss"),
                vehicle_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                vehicle_purchase_value: form.purchaseValue,
                vehicle_alias: form.alias,
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {}),
            };

            return await http.doPost<any, IAssetInfo>("/vehicle/user/insert", body);
        }
    });

    const vehicleUpdateMutation = useMutation({
        mutationFn: async (form: IVehicleHandleForm) => {
            const body = {
                vehicle_type_desc: form.groupType!.name,
                vehicle_type: form.groupType!.id,
                vehicle_fuel_desc: form.fuelType,
                vehicle_fuel_type: form.fuelType!.id,
                vehicle_brand_id: form.brand!.id,
                vehicle_model_id: form.model!.id,
                vehicle_year: moment(form.year?.id, "YYYY-MM").format("YYYY-MM-DD HH:mm:ss"),
                vehicle_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                vehicle_purchase_value: form.purchaseValue,
                vehicle_alias: form.alias
            };

            return await http.doPatch<any, IAssetInfo>(`/vehicle/user/${props.assetId}`, body);
        }
    });

    const fuelData = [
        {
            key: "Gasolina",
            label: "Gasolina",
            id: 1
        },
        {
            key: "Álcool",
            label: "Álcool",
            id: 2
        },
        {
            key: "Flex",
            label: "Flex",
            id: 5
        },
        {
            key: "Diesel",
            label: "Diesel",
            id: 3
        },
        {
            key: "Híbrido",
            label: "Híbrido",
            id: 6
        },
        {
            key: "Elétrico",
            label: "Elétrico",
            id: 4
        }
    ];

    const vehicleTypes = [
        {
            id: 1,
            name: "Automóvel"
        },
        {
            id: 2,
            name: "Caminhão/Ônibus"
        },
        {
            id: 3,
            name: "Motocicleta"
        }
    ];

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    useEffect(() => {
        if (!vehicleDetailsQuery.data?.vehicle_user) {
            return;
        }

        const vehicle = vehicleDetailsQuery.data;

        const fuel = fuelData.find(f => f.id == vehicle!.vehicle_user.vehicle_fuel_type);
        const vehicleType = vehicleTypes.find(v => v.id == vehicle!.vehicle_user.vehicle_type);
        formik.setValues(() => ({
            groupType: {
                id: vehicleType?.id ?? 0,
                name: vehicleType?.name ?? ""
            },
            fuelType: {
                id: fuel?.id ?? 0,
                name: fuel?.label ?? ""
            },
            brand: {
                id: vehicle!.vehicle_user.vehicle_brand_id,
                name: vehicle!.vehicle_user.vehicle_model.brand.vehicle_model_brand_label,
                code: vehicle!.vehicle_user.vehicle_model.brand.vehicle_model_brand_value
            },
            model: {
                id: vehicle!.vehicle_user.vehicle_model.id,
                name: vehicle!.vehicle_user.vehicle_model.vehicle_model_label,
                code: 0,
                years: vehicle!.vehicle_user.vehicle_model.years
            },
            year: {
                id: vehicle!.vehicle_user.vehicle_model.years.find(year => year.mdl_year_value.startsWith(new Date(vehicle!.vehicle_user.vehicle_year).getFullYear().toString()))?.mdl_year_value ?? "",
                label: vehicle!.vehicle_user.vehicle_model.years.find(year => year.mdl_year_value.startsWith(new Date(vehicle!.vehicle_user.vehicle_year).getFullYear().toString()))?.mdl_year_label ?? ""
            },
            alias: vehicle!.vehicle_user.vehicle_alias,
            purchaseValue: vehicle!.vehicle_user.vehicle_purchase_value,
            purchaseDate: moment(vehicle!.vehicle_user.vehicle_purchase_date).toDate()
        }))
    }, [vehicleDetailsQuery.data]);

    useEffect(() => {
        if (!props.initialData) {
            return;
        }

        const data = props.initialData;
        const fuel = fuelData.find(f => f.id == data.vehicle_fuel_type);
        const vehicleType = vehicleTypes.find(v => v.id == data.vehicle_type);

        formik.setValues({
            groupType: vehicleType ? {
                id: vehicleType.id,
                name: vehicleType.name
            } : undefined,
            fuelType: fuel ? {
                id: fuel.id,
                name: fuel.label
            } : undefined,
            brand: data.vehicle_brand_id ? {
                id: data.vehicle_brand_id,
                name: data.vehicle_brand_name,
                code: data.vehicle_brand_id
            } : undefined,
            model: data.vehicle_model_id ? {
                id: data.vehicle_model_id,
                name: data.vehicle_model_name,
                code: 0,
                years: []
            } : undefined,
            year: data.vehicle_year ? {
                id: data.vehicle_year,
                label: data.vehicle_year.split("-")[0]
            } : undefined,
            alias: data.vehicle_alias || "",
            purchaseValue: data.vehicle_purchase_value || 0,
            purchaseDate: data.vehicle_purchase_date ? moment(data.vehicle_purchase_date).toDate() : undefined
        });
    }, [props.initialData]);

    const send = async (form: IVehicleHandleForm) => {
        if (!form || vehicleInsertMutation.isPending || vehicleUpdateMutation.isPending) {
            return;
        }

        try {
            let assetInfo;
            if (isEdit) {
                assetInfo = await vehicleUpdateMutation.mutateAsync(form);
            } else {
                assetInfo = await vehicleInsertMutation.mutateAsync(form);
            }
            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={vehicleDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!vehicleDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Veículo</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <View flexColumn>
                        <Text>
                            Escolha um tipo
                        </Text>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.errors.groupType && formik.touched.groupType}
                                group={vehicleTypes.map(g => (
                                    {
                                        name: g.name,
                                        disabled: isEdit,
                                        selected: formik.values.groupType?.id == g.id,
                                        onClick: () => formik.setFieldValue("groupType", g)
                                    })) ?? []
                                }
                            />
                        </View>

                        {
                            !!formik.errors.groupType && formik.touched.groupType && (
                                <Text className="text-red-500 mt-2" size="xs">{formik.errors.groupType}</Text>
                            )
                        }
                    </View>

                    <VehicleBrandSearchSelector
                        selectedVehicleBrand={formik.values.brand}
                        selectVehicleBrand={(v) => formik.setFieldValue("brand", v)}
                        disabled={isEdit}
                        hasError={!!formik.errors.brand && formik.touched.brand}
                        error={formik.errors.brand}
                    />

                    <VehicleModelSearchSelector
                        brandCode={formik.values.brand?.code}
                        selectedVehicleModel={formik.values.model}
                        disabled={isEdit || !!formik.errors.brand}
                        selectVehicleModel={(v) => formik.setFieldValue("model", v)}
                        hasError={!!formik.errors.model && formik.touched.model}
                        error={formik.errors.model}
                    />

                    <View flexColumn>
                        <Text>
                            Qual o combustível?
                        </Text>

                        <View
                            className={`flex flex-row flex-wrap items-center mt-3 gap-2 rounded`}
                        >
                            <ButtonGroup
                                invalid={!!formik.errors.fuelType && formik.touched.fuelType}
                                group={fuelData.map(g => (
                                    {
                                        name: g.label,
                                        disabled: false,
                                        selected: formik.values.fuelType?.name == g.label,
                                        onClick: () => formik.setFieldValue("fuelType", { id: g.id, name: g.label })
                                    })) ?? []
                                }
                            />
                        </View>

                        {
                            !!formik.errors.fuelType && formik.touched.fuelType && (
                                <Text className="text-red-500 mt-2" size="xs">{formik.errors.fuelType}</Text>
                            )
                        }
                    </View>

                    <FormField
                        id="year"
                        hasError={!!formik.errors.year && formik.touched.year}
                        error={formik.errors.year}
                        label="Ano/Modelo"
                    >
                        <Dropdown
                            value={formik.values.year}
                            onChange={(e) => {
                                formik.setFieldValue("year", e.value);
                            }}
                            options={formik.values.model?.years.map(year => ({ id: year.mdl_year_value, label: year.mdl_year_label })) ?? []}
                            optionLabel="label"
                            optionValue="id"
                            placeholder="Informe primeiro um modelo"
                            className="w-full"
                        />
                    </FormField>

                    <FormField
                        id="purchaseDate"
                        label="Data de compra"
                        hasError={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                        error={formik.errors.purchaseDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                            value={formik.values.purchaseDate}
                            dateFormat="dd/mm/yy"
                            readOnlyInput
                            showIcon
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                formik.setFieldValue("purchaseDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <FormField
                        id="purchaseValue"
                        hasError={!!formik.errors.purchaseValue && formik.touched.purchaseValue}
                        error={formik.errors.purchaseValue}
                        label="Valor de compra"
                    >
                        <InputNumber
                            invalid={!!formik.errors.purchaseValue && formik.touched.purchaseValue}
                            value={formik.values.purchaseValue}
                            onValueChange={(e) =>
                                formik.setFieldValue("purchaseValue", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <FormField
                        id="alias"
                        hasError={!!formik.errors.alias && formik.touched.alias}
                        error={formik.errors.alias}
                        label="Apelido"
                    >
                        <InputText
                            invalid={!!formik.errors.alias && formik.touched.alias}
                            value={formik.values.alias}
                            onChange={(e) =>
                                formik.setFieldValue("alias", e.target.value)
                            }
                        />
                    </FormField>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={vehicleInsertMutation.isPending || vehicleUpdateMutation.isPending}
                            disabled={vehicleInsertMutation.isPending || vehicleUpdateMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show>
        </>
    );
}

export default VehicleHandleForm;
